import { Select, Table, TableColumnsType } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  getPrtItems,
  getTestTypes,
  updatePrtItems,
} from '../../api/testCenter.api';
import SimpleHeader from '../../components/layout/SimpleHeader';
import useAxios from '../../hooks/useAxios';
import {
  IGetPrtItems,
  IGetTestTypes,
  IPrtItem,
  ITestType,
} from '../../models/testCenter.model';
import Button from '../../components/Button';
import _ from 'lodash';
import Loader from '../../components/Loader';
import ErrorResponse from '../../components/ErrorResponse';
import useNotification from '../../hooks/useNotification';
import useAuth from '../../hooks/useAuth';

const Block = styled.div`
  .main {
    width: 600px;
  }
`;

const CbtPrt = () => {
  const { t } = useTranslation();
  const { writeAuthYn } = useAuth();
  const { successNotification, infoNotification } = useNotification();
  const { data: res, isLoading, error, execute } = useAxios<IGetTestTypes>();
  const {
    data: res2,
    isLoading: isLoading2,
    error: error2,
    execute: execute2,
  } = useAxios<IGetPrtItems>();
  const {
    data: updateRes,
    isLoading: updateIsLoading,
    error: updateError,
    execute: updateExecute,
  } = useAxios();

  const [prts, setPrts] = useState<ITestType[]>([]);
  const [data, setData] = useState<IPrtItem[]>([]);

  useEffect(() => {
    execute(getTestTypes());
    execute2(getPrtItems());
  }, []);

  useEffect(() => {
    if (res && !isLoading) {
      let _data: ITestType[] = _.cloneDeep(res.data);
      _data = _data.filter((item) => item.examDiv === 'PR');
      setPrts(_data);
    }
  }, [res, isLoading]);

  useEffect(() => {
    if (res2 && !isLoading2) {
      let _data: IPrtItem[] = _.cloneDeep(res2.data);
      _data.forEach((item) => {
        item.status = 'update';
      });
      setData(_data);
    }
  }, [res2, isLoading2]);

  useEffect(() => {
    if (updateRes && !updateIsLoading) {
      successNotification(t('common.message.save1'));
    }
  }, [updateRes, updateIsLoading]);

  const onAddRow = () => {
    const _data: IPrtItem[] = [...data];
    _data.push({
      status: 'create',
      srtScore: null,
      endScore: null,
      examTypeCode: null,
    });
    setData(_data);
  };

  const onSetData = (index: number, key: string, value: string) => {
    const _data: any = [...data];
    _data[index][key] = value;
    setData(_data);
  };

  const onSubmit = () => {
    let prevEndScore: number | null = null;
    for (let i = 0; i < data.length; i++) {
      if (
        data[i].srtScore === null ||
        data[i].endScore === null ||
        data[i].srtScore === '' ||
        data[i].endScore === '' ||
        !data[i].examTypeCode
      ) {
        return infoNotification(t('cbt.class.message1'));
      }
      if (prevEndScore && !(Number(data[i].srtScore) === prevEndScore + 1)) {
        return infoNotification(t('cbt.class.message2'));
      }
      if (i === 0) {
        if (Number(data[i].srtScore) !== 0) {
          return infoNotification(t('cbt.class.message2'));
        }
      }
      if (Number(data[i].srtScore) <= Number(data[i].endScore)) {
        return infoNotification(t('cbt.class.message2'));
      }
      prevEndScore = Number(data[i].endScore);
    }

    updateExecute(updatePrtItems(data));
  };

  const columns: TableColumnsType<any> = [
    {
      title: t('cbt.prt.column1'),
      dataIndex: 'prtLevelCode',
      key: 'prtLevelCode',
      align: 'center',
      render: (value, record, index) => (
        <div className="flex flex-justify-center">
          <div className="w-100p">
            <input
              type="number"
              className="form-control"
              value={record.srtScore}
              onChange={(e) => onSetData(index, 'srtScore', e.target.value)}
            />
          </div>
          <span className="v-line">&nbsp;~&nbsp;</span>
          <div className="w-100p">
            <input
              type="number"
              className="form-control"
              value={record.endScore}
              onChange={(e) => onSetData(index, 'endScore', e.target.value)}
            />
          </div>
        </div>
      ),
    },
    {
      title: 'Pre Test',
      dataIndex: 'examTypeCode',
      key: 'examTypeCode',
      align: 'center',
      render: (value, record, index) => (
        <div className="w-100">
          <Select
            className="w-100"
            placeholder={t('common.button.select')}
            value={record.examTypeCode}
            onChange={(value) => onSetData(index, 'examTypeCode', value)}
          >
            {prts?.map((item: any) => {
              return (
                <Select.Option value={item.examTypeCode}>
                  {item.examTypeName}
                </Select.Option>
              );
            })}
          </Select>
        </div>
      ),
    },
  ];

  return (
    <>
    <Block className="content scrollbar">
      {isLoading || isLoading2 || updateIsLoading ? <Loader /> : null}
      <ErrorResponse error={error} />
      <ErrorResponse error={error2} />
      <ErrorResponse error={updateError} />
      <SimpleHeader
        iconFile="icon-cbt-big.svg"
        parentMenu={t('common.menu.cbt')}
        currentMenu={t('cbt.prt.title1')}
        onlyTitle={true}
      />

      <div className="col-frame">
        <div className="comm-bx__between">
          <div></div>
          <div className="comm-btn__area">
            <Button
              className="btn-basic pink-line"
              onClick={onAddRow}
              text={t('cbt.class.button1')}
              writeAuthYn={writeAuthYn}
            />
          </div>
        </div>
        <div className='comm-table__area'>
          <Table
            columns={columns}
            dataSource={data || undefined}
            pagination={{
              pageSize: 99999,
            }}
          />
        </div>
        <div className='comm-bx__space'>
          <div className='comm-btn__area right'>
            <Button
              className="btn-basic pink"
              onClick={onSubmit}
              text={t('common.button.save')}
              writeAuthYn={writeAuthYn}
              disabled
            />
            <Button
              className="btn-basic pink-line"
              onClick={onAddRow}
              text={t('cbt.class.button1')}
              writeAuthYn={writeAuthYn}
            />
          </div>
        </div>
      </div>
    </Block>
    </>
  );
};

export default CbtPrt;
